import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { DocumentTree, TreeCategory, TreeAction } from '../components/document-library/DocumentTree';
import { ContentViewer, DocumentDetail } from '../components/document-library/ContentViewer';
import { DocumentEditor } from '../components/document-library/DocumentEditor';
import { DocumentSearch } from '../components/document-library/DocumentSearch';

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    height: 'calc(100vh - 64px)',
    overflow: 'hidden',
  },
  sidebar: {
    width: '300px',
    minWidth: '300px',
    backgroundColor: '#f9fafb',
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sidebarHeader: {
    padding: '1rem 1rem 0.5rem 1rem',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  sidebarTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: '0 0 0.75rem 0',
  },
  treeContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '0.75rem 1rem',
  },
  content: {
    flex: 1,
    backgroundColor: '#fff',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#888',
  },
};

function filterTree(tree: TreeCategory[], query: string): TreeCategory[] {
  if (!query) return tree;
  const lower = query.toLowerCase();

  return tree
    .map((cat) => {
      const filteredModules = cat.modules
        .map((mod) => {
          const filteredDocs = mod.documents.filter((doc) =>
            doc.title.toLowerCase().includes(lower)
          );
          if (filteredDocs.length > 0) {
            return { ...mod, documents: filteredDocs };
          }
          // Also match module name
          if (mod.name.toLowerCase().includes(lower)) {
            return mod;
          }
          return null;
        })
        .filter(Boolean) as TreeCategory['modules'];

      if (filteredModules.length > 0) {
        return { ...cat, modules: filteredModules };
      }
      // Also match category name
      if (cat.name.toLowerCase().includes(lower)) {
        return cat;
      }
      return null;
    })
    .filter(Boolean) as TreeCategory[];
}

export function DocumentLibraryPage() {
  const [treeData, setTreeData] = useState<TreeCategory[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editingDoc, setEditingDoc] = useState<DocumentDetail | null>(null);
  const [createModuleId, setCreateModuleId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchTree = useCallback(async () => {
    try {
      const data = await apiClient<TreeCategory[]>('/documents/tree');
      setTreeData(data);
    } catch {
      // Silently fail — tree will be empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSelect = (docId: string) => {
    setSelectedDocumentId(docId);
    setEditMode(false);
    setEditingDoc(null);
    setCreateModuleId(null);
  };

  const handleEdit = (doc: DocumentDetail) => {
    setEditingDoc(doc);
    setEditMode(true);
    setCreateModuleId(null);
  };

  const handleSave = () => {
    setEditMode(false);
    setEditingDoc(null);
    setCreateModuleId(null);
    fetchTree();
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditingDoc(null);
    setCreateModuleId(null);
  };

  const handleAction = async (action: TreeAction) => {
    switch (action.type) {
      case 'addCategory': {
        const name = window.prompt('Enter category name:');
        if (!name?.trim()) return;
        try {
          await apiClient('/documents/categories', {
            method: 'POST',
            body: { name: name.trim() },
          });
          fetchTree();
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to create category';
          alert(message);
        }
        break;
      }

      case 'addModule': {
        const name = window.prompt('Enter module name:');
        if (!name?.trim()) return;
        try {
          await apiClient('/documents/modules', {
            method: 'POST',
            body: { name: name.trim(), categoryId: action.categoryId },
          });
          fetchTree();
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to create module';
          alert(message);
        }
        break;
      }

      case 'addDocument': {
        setCreateModuleId(action.moduleId);
        setEditingDoc(null);
        setEditMode(true);
        break;
      }

      case 'renameCategory': {
        const name = window.prompt('Rename category:', action.currentName);
        if (!name?.trim() || name.trim() === action.currentName) return;
        try {
          await apiClient(`/documents/categories/${action.categoryId}`, {
            method: 'PATCH',
            body: { name: name.trim() },
          });
          fetchTree();
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to rename category';
          alert(message);
        }
        break;
      }

      case 'renameModule': {
        const name = window.prompt('Rename module:', action.currentName);
        if (!name?.trim() || name.trim() === action.currentName) return;
        try {
          await apiClient(`/documents/modules/${action.moduleId}`, {
            method: 'PATCH',
            body: { name: name.trim() },
          });
          fetchTree();
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to rename module';
          alert(message);
        }
        break;
      }

      case 'editDocument': {
        setSelectedDocumentId(action.documentId);
        // Fetch the document to edit
        try {
          const doc = await apiClient<DocumentDetail>(`/documents/${action.documentId}`);
          setEditingDoc(doc);
          setEditMode(true);
          setCreateModuleId(null);
        } catch {
          alert('Failed to load document for editing');
        }
        break;
      }

      case 'deleteCategory': {
        if (action.hasChildren) {
          alert('Cannot delete — this category has modules. Remove all modules first.');
          return;
        }
        if (!window.confirm('Delete this category?')) return;
        try {
          await apiClient(`/documents/categories/${action.categoryId}`, {
            method: 'DELETE',
          });
          fetchTree();
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to delete category';
          alert(message);
        }
        break;
      }

      case 'deleteModule': {
        if (action.hasChildren) {
          alert('Cannot delete — this module has documents. Remove all documents first.');
          return;
        }
        if (!window.confirm('Delete this module?')) return;
        try {
          await apiClient(`/documents/modules/${action.moduleId}`, {
            method: 'DELETE',
          });
          fetchTree();
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to delete module';
          alert(message);
        }
        break;
      }

      case 'deleteDocument': {
        if (!window.confirm('Delete this document?')) return;
        try {
          await apiClient(`/documents/${action.documentId}`, {
            method: 'DELETE',
          });
          if (selectedDocumentId === action.documentId) {
            setSelectedDocumentId(null);
            setEditMode(false);
            setEditingDoc(null);
          }
          fetchTree();
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to delete document';
          alert(message);
        }
        break;
      }
    }
  };

  const displayTree = filterTree(treeData, searchQuery);

  if (loading) {
    return <div style={styles.loading}>Loading Document Library...</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h2 style={styles.sidebarTitle}>📚 Documents</h2>
          <DocumentSearch onSearch={handleSearch} />
        </div>
        <div style={styles.treeContainer}>
          <DocumentTree
            treeData={displayTree}
            selectedId={selectedDocumentId}
            onSelect={handleSelect}
            onAction={handleAction}
          />
        </div>
      </div>

      <div style={styles.content}>
        {editMode ? (
          <DocumentEditor
            document={editingDoc}
            moduleId={createModuleId}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        ) : (
          <ContentViewer
            documentId={selectedDocumentId}
            onEdit={handleEdit}
          />
        )}
      </div>
    </div>
  );
}
